import React, { useMemo, useRef } from "react";
import {
  ButtonGhostCompact,
  CONTENT_BODY_POSTIT,
  Divider,
  OKELink,
  SkeletonLoader,
  UI_BODY_SM_DF as UI_BODY_SM_DF_JSX,
  UI_CAPTION_DF as UI_CAPTION_DF_JSX,
  UI_BODY_BOLD_SM_DF as UI_BODY_BOLD_SM_DF_JSX,
  UI_CAPTION,
  UI_TAG,
  colors2,
  typoV2_CSS,
} from "oolib";
import { AIMessageStyled } from "./styled";
import Markdown from "react-markdown";
import remarkGfm from 'remark-gfm'; 
import { Copy } from "../../UserActions/Copy";
import { Feedback } from "../../UserActions/Feedback";

import { SummaryWrapperStyled } from "../../../../Synthesis/comps/AnnotationCard/styled";
import styled from "styled-components";
import { useSidebarContext } from "../../../contexts/sidebarContext";
import { LoadingText } from "../Loader";
// import { WidgetBarChart } from "@/components/analysisModules/reports/ReportLanding/comps/WidgetComps/_EXPORTS";
import { Component as WidgetBarChart } from "@/components/analysisModules/reports/ReportLanding/comps/WidgetComps/WidgetBarChart";
import { useGetAnnotations } from "@/utils/react-query-hooks/getData";
import { getRoutePathToPublishedContent } from "@okf/ootils";
import { __GetContentTypeConfigNew } from "@/utils/getters/gettersV2";
import { useGetQueryData } from "@/utils/react-query-hooks/general";
import { FollowUpChips } from "../../FollowUpChips";
import { TopicFactsStrip } from "../../TopicFactsStrip";
import { splitFindings } from "./findings.js";

const {
  UI_BODY_SM_DF,
  UI_BODY_BOLD_SM_DF,
  UI_BODY_DF,
  UI_BODY_BOLD_DF,
  UI_DISPLAY,
  UI_DISPLAY_SM,
  UI_HEADLINE,
  UI_HEADLINE_SM,
  UI_TITLE,
  UI_TITLE_SM,
  UI_CAPTION_DF,
} = typoV2_CSS;

const StyledOverrideWrapperStyled = styled.div`
  .viz-area {
    padding-left: 0 !important;
    padding-right: 0 !important;
  }
`;

export const MarkdownStyled = styled(Markdown)`
  height: 100%;
  font-family: inherit;
  font-size: inherit;
  font-weight: inherit;
  line-height: inherit;

  > *:first-child {
    margin-top: 0;
  }

  ol {
    margin-top: 0; //god knows where it comes from but we have to cancel it
    padding-left: 1.6rem;
    li {
      list-style: decimal;
    }

    /* A finding is one list item: its claim, body, quotes and closing line all
       sit under its own number. Spacing separates findings from each other more
       than the blocks within one finding. */
    > li + li {
      margin-top: 1.4rem;
    }
    > li > p {
      margin: 0.4rem 0;
    }
    > li > p:first-child {
      margin-top: 0;
    }
    > li > blockquote {
      margin: 0.6rem 0;
    }
  }

  ul {
    li {
      list-style: disc;

      li {
        list-style: circle;
      }
    }
  }

  p,
  li {
    ${UI_BODY_SM_DF} // UI_BODY_DF

    strong {
      ${UI_BODY_BOLD_SM_DF};
    }
  }

  /*  h1, h2, h3, and blockquote */
  h1,
  h2,
  h3,
  blockquote {
    margin: 0.9rem 0;
  }

  /* for first child h1, h2, h3, and blockquote */
  h1:first-child,
  h2:first-child,
  h3:first-child,
  blockquote:first-child {
    margin-top: 0;
    margin-bottom: 0.9rem;
  }

  /* for h1, h2, h3, and blockquote followed by a p, ul, or ol */
  h1 + p,
  h2 + p,
  h3 + p,
  blockquote + p,
  h1 + ul,
  h2 + ul,
  h3 + ul,
  blockquote + ul,
  h1 + ol,
  h2 + ol,
  h3 + ol,
  blockquote + ol {
    margin-top: -0.65rem; /* 28px - 20px = 8px */
  }

  li {
    margin-top: 0.2rem;
    margin-bottom: 0.2rem;
  }
  p {
    margin-bottom: 0.4rem;
  }

  /* Quotes from the data: a quiet left rule, no italics (verbatim text). */
  blockquote {
    border-left: 3px solid ${colors2.grey20};
    padding: 0.2rem 0 0.2rem 0.8rem;
    color: ${colors2.grey80};
    p {
      margin-bottom: 0;
    }
  }

  /* A quote's attribution ("— Chaupal 7") sits below the quote in the
     hierarchy: smaller, grey, never bold. Rendered from a #src link. */
  .quote-source {
    ${UI_CAPTION_DF}
    color: ${colors2.grey60};
    white-space: nowrap;
  }

  /* Claim-level citation marks ([Sn] → an inline reference chip). Not a
     superscript: a raised mark inside a wrapping blockquote line floated
     above the text and read as a stray digit. The chip sits on the text's
     midline, carries a link glyph so it reads as "open reference n", and
     keeps the line height unchanged. */
  .cite {
    display: inline-block;
    vertical-align: middle;
    margin: -0.1em 0.1em 0 0.35em;
    line-height: 1;
  }
  .cite button {
    font: inherit;
    font-size: 0.72em;
    font-weight: 600;
    line-height: 1;
    height: 1.5em;
    display: inline-flex;
    align-items: center;
    gap: 0.28em;
    padding: 0 0.5em 0 0.4em;
    border-radius: 999px;
    border: 1px solid ${colors2.grey20};
    background: ${colors2.grey5};
    color: ${colors2.grey80};
    cursor: pointer;
    white-space: nowrap;
  }
  .cite button svg {
    width: 0.9em;
    height: 0.9em;
    flex: 0 0 auto;
  }
  .cite button:hover {
    background: ${colors2.grey10};
    border-color: ${colors2.grey40};
  }

  h1 {
    ${UI_HEADLINE_SM}
  }
  h2 {
    ${UI_TITLE}
  }
  h3 {
    ${UI_TITLE_SM}
  }
  h4 {
    ${UI_BODY_DF}
  }
  h5 {
    ${UI_TITLE_SM}
  }
  h6 {
    ${UI_TITLE_SM}
  }

  table {
    border-collapse: collapse;
    width: 100%;
    margin: 1rem 0;
  }

  th, td {
    border: 1px solid ${colors2.grey10};
    padding: 8px;
    text-align: left;
  }

  th {
    background-color: ${colors2.grey5};
    font-weight: bold;
  }

  tr:nth-child(even) {
    background-color: ${colors2.grey2};
  }
`;

// [S3] in the stored answer → a markdown link to #cite-3, which the custom
// link renderer turns into a superscript mark. Streamed text goes through
// the same path, so marks appear as the answer forms (clickable once the
// message is stored and its citations are known).
const CITE_HREF = "#cite-";
const withCitationLinks = (text) => text.replace(/\[S(\d+)\]/g, (m, n) => `[${n}](${CITE_HREF}${n})`);

// The source after a quote ("— Chaupal 7", optionally followed by [Sn]) is
// turned into a link with a sentinel href, so the link renderer below can
// draw it muted. Only on blockquote lines; only the last dash on the line.
const SRC_HREF = "#src";
const withSourceLabels = (text) =>
  text
    .split("\n")
    .map((line) =>
      /^\s*>/.test(line)
        ? line.replace(/\s—\s([^—\[\]]{1,120}?)(\s*(?:\[S\d+\]\s*)*)$/, (m, label, cites) => ` — [${label.trim()}](${SRC_HREF})${cites}`)
        : line
    )
    .join("\n");

const CitationMark = ({ n, onClick }) => (
  <span className="cite">
    <button type="button" onClick={onClick ? () => onClick(Number(n)) : undefined} title={`Open reference ${n}`} aria-label={`Open reference ${n}`}>
      <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M4 2.5H2.5v7h7V8" />
        <path d="M6.5 2.5h3v3M9.5 2.5 5.5 6.5" />
      </svg>
      {n}
    </button>
  </span>
);

const Md = ({ text, onCitationClick }) => {
  const renderers = {
    link: ({ href, children: linkChildren }) =>
      href && href.startsWith(CITE_HREF) ? (
        <CitationMark n={href.slice(CITE_HREF.length)} onClick={onCitationClick} />
      ) : href === SRC_HREF ? (
        <span className="quote-source">{linkChildren}</span>
      ) : (
        <a href={href} target="_blank" rel="noreferrer">
          {linkChildren}
        </a>
      ),
  };
  return (
    <MarkdownStyled plugins={[remarkGfm]} renderers={renderers}>
      {withCitationLinks(withSourceLabels(text))}
    </MarkdownStyled>
  );
};

/* A finding is a row: its number in a gutter, everything that belongs to it —
   claim, body, quotes, closing line — in the column beside it. Rendering the
   numbers here rather than as a markdown list is what keeps the hierarchy: the
   writer emits a finding's quotes flush left, and markdown reads those as
   siblings of the list, which puts them beside the findings instead of under. */
const AnswerStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
`;

const FindingsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.4rem;
`;

const FindingRow = styled.div`
  display: grid;
  grid-template-columns: 1.5rem 1fr;
  column-gap: 0.5rem;
  align-items: start;
`;

const FindingNumber = styled.div`
  ${UI_BODY_SM_DF}
  color: ${colors2.grey60};
  text-align: right;
  font-variant-numeric: tabular-nums;
`;

const FindingBody = styled.div`
  min-width: 0;
`;

/**
 * @param onCitationClick (n) => void — opens the reference the mark points at
 */
export const MarkdownRenderer = ({ children, onCitationClick = null }) => {
  const text = typeof children === "string" ? children : "";
  const { intro, findings } = useMemo(() => splitFindings(text), [text]);
  if (!findings.length) return <Md text={text} onCitationClick={onCitationClick} />;
  return (
    <AnswerStack>
      {intro ? <Md text={intro} onCitationClick={onCitationClick} /> : null}
      <FindingsList>
        {findings.map((f) => (
          <FindingRow key={f.n}>
            <FindingNumber>{f.n}.</FindingNumber>
            <FindingBody>
              <Md text={f.body} onCitationClick={onCitationClick} />
            </FindingBody>
          </FindingRow>
        ))}
      </FindingsList>
    </AnswerStack>
  );
};

/** One footer under every answer: facts, references, actions + chips. */
const FooterStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  padding-top: 0.2rem;
`;

const ActionsRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
`;

// noBorderShadow & hideReferencesAndActions props used by AskAI widget
// directLinkReferences: shows all refs in horizontal scroll with direct navigation links (no sidebar)
// onFollowUp + showFollowUps: the chat passes these for the LAST assistant
// message so its follow-up chips (metadata.followUps, split tenants) render
// and send with their anchor. The widget passes neither.
export const AIMessage = ({ message, streaming = false, loading, activeChatId, noBorderShadow = false, hideReferencesAndActions = false, directLinkReferences = false, onFollowUp = null, showFollowUps = false }) => {
  const { setShowSidebar, setContent, content } = useSidebarContext();
  const markdownRef = useRef();
  const relatedDocumentFields = message.relatedDocumentFields || [];
  const relatedDocumentFieldsExist = relatedDocumentFields.length > 0;

  const handleViewAll = ({ relatedChunks, relatedDocumentFields }) => {
    setShowSidebar({ relatedChunks, relatedDocumentFields });
    setContent(message);
  };

  //vectorSearchInfo is depr. this is backwards compat
  const chunkIds = message.relatedChunks?.length > 0
    ? message.relatedChunks
    : message.vectorSearchInfo?.chunks.map((d) => {
        return d._id; //with new structure, we will pass _ids directly in the array
      });

  //vectorSearchInfo is depr. this is backwards compat
  const relatedChunksExist = message.relatedChunks?.length > 0 || message.vectorSearchInfo?.chunks?.length > 0
    

  const {
    data: chunkResults,
    status: chunksFetchStatus,
    refetch: refetchChunks,
  } = useGetAnnotations({
    findQuery: {
      _id: {
        $in: chunkIds,
      },
    },
    dataSource: "chunks",
    queryOptions: {
      enabled: relatedChunksExist,
    },
  });

  // BUG WORKAROUND: when AI chat uses the searchAnnotation tool (instead of
  // searchChunks), the backend stores annotation document _ids in
  // message.relatedChunks. Those won't resolve against the chunks collection,
  // so fall back to fetching the missing ids from the annotations collection.
  // See backend bug note: ai/AIChat_v3/nodes/storeResponseNode.js
  const missingIds = useMemo(() => {
    if (chunksFetchStatus !== "success" || !chunkIds?.length) return [];
    const found = new Set((chunkResults?.data || []).map((d) => d._id));
    return chunkIds.filter((id) => !found.has(id));
  }, [chunkResults, chunkIds, chunksFetchStatus]);

  const {
    data: annotationResults,
    status: annotationsFetchStatus,
    refetch: refetchAnnotations,
  } = useGetAnnotations({
    findQuery: {
      _id: {
        $in: missingIds,
      },
    },
    dataSource: "annotations",
    queryOptions: {
      enabled: missingIds.length > 0,
    },
  });

  // Preserve the { data: [...] } shape that downstream renderers expect.
  // Sort by the STORED id order: the backend ranks references by relevance
  // to the answer (reference rerank), but the $in fetch returns database
  // order — without this sort, "Top References" would be arbitrary.
  const relatedChunks = useMemo(() => {
    const combined = [
      ...(chunkResults?.data || []),
      ...(annotationResults?.data || []),
    ];
    if (chunkIds?.length) {
      const orderById = new Map(chunkIds.map((id, i) => [String(id), i]));
      combined.sort(
        (a, b) =>
          (orderById.get(String(a._id)) ?? Infinity) -
          (orderById.get(String(b._id)) ?? Infinity)
      );
    }
    return { data: combined };
  }, [chunkResults, annotationResults, chunkIds]);

  const relatedChunksFetchStatus =
    missingIds.length > 0 ? annotationsFetchStatus : chunksFetchStatus;

  const refetchRelatedChunks = () => {
    refetchChunks();
    if (missingIds.length > 0) refetchAnnotations();
  };

  

  const handleCopy = async () => {
    if (!markdownRef.current) return;

    const htmlContent = markdownRef.current.innerHTML; // Rendered HTML
    const plainText = markdownRef.current.textContent; // Plain text

    // Create clipboard data with all formats
    const clipboardItem = new ClipboardItem({
      "text/html": new Blob([htmlContent], { type: "text/html" }),
      "text/plain": new Blob([plainText], { type: "text/plain" }),
    });

    await navigator.clipboard.write([clipboardItem]);
  };

  const genVizIfApplicable = ({ vizData }) => {
    if (!vizData) return null;
    // Empty aggregation results (e.g. the agent probed a source with no
    // documents) are not charts — rendering them shows a wall of
    // "No data available" headers above the answer.
    let toRender = vizData.filter(
      (v) =>
        v.name === "aggregateMetricsAcrossDimensions" &&
        Array.isArray(v.response) &&
        v.response.length > 0
    );
    return toRender.map((v, i) => (
      <StyledOverrideWrapperStyled key={i}>
        {v.args?.reasonToUse && (
          <UI_BODY_BOLD_SM_DF_JSX style={{ padding: "0.5rem 0" }}>
            {v.args.reasonToUse}
          </UI_BODY_BOLD_SM_DF_JSX>
        )}
        <WidgetBarChart data={v.response} summarizeAfterIdx={7} />
      </StyledOverrideWrapperStyled>
    ));
  };

  // Citation marks open the reference panel (all references, cited ones first).
  // Citation marks in the prose open the reference panel. The cards carry no
  // number of their own — storeResponseNode already orders cited sources first.
  const citations = Array.isArray(message?.metadata?.citations) ? message.metadata.citations : [];
  const handleCitationClick = () => handleViewAll({ relatedChunks, relatedDocumentFields });

  const showFacts = !!message?.metadata?.topicFacts && !hideReferencesAndActions && !loading;
  const showRefs = (relatedChunksExist || relatedDocumentFieldsExist) && !hideReferencesAndActions;
  const showChips = showFollowUps && !loading && (message?.metadata?.followUps || []).length > 0;
  // Actions render only in the sidebar variant; the widget's direct-link mode
  // suppresses them, so it must not count toward "the footer has content".
  const showActions = !hideReferencesAndActions && !directLinkReferences;
  const hasFooter = !loading && !streaming && (showFacts || showRefs || showChips || showActions);

  return (
    <AIMessageStyled
      active={content ? message._id === content._id : false}
      key={message._id}
      noBorderShadow={noBorderShadow}
    >
      {loading ? (
        <LoadingText />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "0" }}>
          {genVizIfApplicable({ vizData: message.content.viz })}
          <UI_BODY_SM_DF_JSX>
            <div ref={markdownRef}>
              <MarkdownRenderer onCitationClick={citations.length ? handleCitationClick : null}>
                {streaming ? message : message.content.value}
              </MarkdownRenderer>
            </div>
          </UI_BODY_SM_DF_JSX>
        </div>
      )}

      {/* One footer, one rhythm: the facts the answer was written from, the
          references it rests on, then actions and follow-ups on one row. */}
      {hasFooter && (
        <>
          <Divider />
          <FooterStack>
            {showFacts && <TopicFactsStrip facts={message.metadata.topicFacts} />}
            {showRefs &&
              (directLinkReferences ? (
                <DirectLinkRefs
                  relatedChunks={relatedChunks}
                  relatedChunksFetchStatus={relatedChunksFetchStatus}
                  refetchRelatedChunks={refetchRelatedChunks}
                  relatedDocumentFields={relatedDocumentFields}
                />
              ) : relatedChunksExist ? (
                <Chunks
                  handleViewAll={handleViewAll}
                  relatedChunks={relatedChunks}
                  relatedChunksFetchStatus={relatedChunksFetchStatus}
                  refetchRelatedChunks={refetchRelatedChunks}
                  relatedDocumentFields={relatedDocumentFields}
                />
              ) : (
                <DocumentFieldRefs relatedDocumentFields={relatedDocumentFields} handleViewAll={handleViewAll} relatedChunks={relatedChunks} />
              ))}
            {showActions && (
              <ActionsRow>
                <Actionables handleCopy={handleCopy} message={message} activeChatId={activeChatId} />
                {showChips && <FollowUpChips followUps={message.metadata.followUps} onPick={onFollowUp} />}
              </ActionsRow>
            )}
          </FooterStack>
        </>
      )}
    </AIMessageStyled>
  );
};

const Actionables = ({ message, handleCopy, activeChatId }) => {
  return (
    <div style={{ display: "flex", alignItems: "center" }}>
      <Copy onClick={handleCopy} color={"#808080"} />
      {/* <LineVertical size={30} /> */}
      <Feedback
        reaction={message.userFeedback?.reaction}
        comments={message.userFeedback?.comments}
        messageId={message._id}
        activeChatId={activeChatId}
      />
    </div>
  );
};

/** Shared loading/error wrapper for chunk-based reference sections */
const ChunksFetchWrapper = ({ relatedChunksFetchStatus, refetchRelatedChunks, children }) => {
  if (relatedChunksFetchStatus === "loading") {
    return (
      <div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <SkeletonLoader style={{ width: "95px", height: "12px" }} />
        </div>
        <div style={{ display: "flex", gap: "1rem", marginTop: "6px" }}>
          {[1, 2, 3].map((d) => (
            <SkeletonLoader key={d} style={{ height: "68px", flex: 1, borderRadius: "4px" }} />
          ))}
        </div>
      </div>
    );
  }
  if (relatedChunksFetchStatus === "error") {
    return (
      <div
        style={{
          backgroundColor: colors2.redBG, borderRadius: "4px",
          border: `1px solid ${colors2.red}`, display: "flex",
          justifyContent: "center", alignItems: "center", padding: "16px",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <UI_CAPTION_DF_JSX>Failed to load references</UI_CAPTION_DF_JSX>
          <UI_CAPTION_DF_JSX>
            <OKELink onClick={() => refetchRelatedChunks()}>{"Try Again"}</OKELink>
          </UI_CAPTION_DF_JSX>
        </div>
      </div>
    );
  }
  return children;
};

const Chunks = ({
  handleViewAll,
  relatedChunks,
  relatedChunksFetchStatus,
  refetchRelatedChunks,
  relatedDocumentFields = [],
  showMaxLen = 3,
}) => {
  return (
    <ChunksFetchWrapper relatedChunksFetchStatus={relatedChunksFetchStatus} refetchRelatedChunks={refetchRelatedChunks}>
      {(relatedChunksFetchStatus === "success" && relatedChunks?.data?.length > 0 || relatedDocumentFields.length > 0) && (
        <div>
          <div
            onClick={() => handleViewAll({ relatedChunks, relatedDocumentFields })}
            style={{ display: "flex", justifyContent: "space-between", cursor: "pointer" }}
          >
            <UI_TAG>TOP REFERENCES</UI_TAG>{" "}
            <ButtonGhostCompact style={{ marginTop: "-7px" }} icon="ArrowLineUpRight">
              View all references
            </ButtonGhostCompact>
          </div>
          <div style={{ display: "flex", gap: "1rem", marginTop: "6px" }}>
            {relatedChunks?.data?.slice(0, showMaxLen).map((chunk) => {
              // The strip's own compact card: the quote, clamped, over the
              // dataset it came from. The full card (breadcrumb, date, author,
              // tags) lives in the "View all references" sidebar; here it
              // crowded the answer.
              const contentType = chunk.meta?.contentType || chunk.meta?.kp_content_type;
              const datasetTitle = __GetContentTypeConfigNew(contentType)?.general?.content?.title || contentType;
              return (
                <div
                  key={chunk._id}
                  style={{ backgroundColor: "#F3F3F3", borderRadius: "6px", padding: "1rem", flex: 1, minWidth: 0, cursor: "pointer" }}
                  onClick={() => handleViewAll({ relatedChunks, relatedDocumentFields })}
                >
                  {datasetTitle && (
                    <UI_TAG style={{ color: colors2.grey60, display: "block", marginBottom: "0.4rem" }}>{datasetTitle}</UI_TAG>
                  )}
                  <SummaryWrapperStyled noOfLines={3}>
                    <CONTENT_BODY_POSTIT>{chunk.annotations?.fragment?.allText}</CONTENT_BODY_POSTIT>
                  </SummaryWrapperStyled>
                </div>
              );
            })}
            {relatedDocumentFields
              .slice(0, showMaxLen - (relatedChunks?.data?.length || 0))
              .map((docField) => (
                <div
                  key={`${docField.documentId}_${docField.valuePath}`}
                  style={{ backgroundColor: "#F3F3F3", borderRadius: "6px", padding: "1rem", flex: 1, cursor: "pointer" }}
                  onClick={() => handleViewAll({ relatedChunks, relatedDocumentFields })}
                >
                  <SummaryWrapperStyled noOfLines={3}>
                    <UI_BODY_BOLD_SM_DF_JSX>{docField.fieldLabel}</UI_BODY_BOLD_SM_DF_JSX>
                    <UI_CAPTION_DF_JSX style={{ color: colors2.grey60, marginTop: "0.2rem" }}>
                      {docField.documentTitle}
                    </UI_CAPTION_DF_JSX>
                  </SummaryWrapperStyled>
                </div>
              ))}
          </div>
        </div>
      )}
    </ChunksFetchWrapper>
  );
};

const DocumentFieldRefs = ({
  relatedDocumentFields,
  handleViewAll,
  relatedChunks,
  showMaxLen = 3,
}) => {
  if (!relatedDocumentFields || relatedDocumentFields.length === 0) return null;
  return (
    <div>
      <div
        onClick={() => handleViewAll({ relatedChunks, relatedDocumentFields })}
        style={{ display: "flex", justifyContent: "space-between", cursor: "pointer" }}
      >
        <UI_TAG>TOP REFERENCES</UI_TAG>{" "}
        <ButtonGhostCompact style={{ marginTop: "-7px" }} icon="ArrowLineUpRight">
          View all references
        </ButtonGhostCompact>
      </div>
      <div style={{ display: "flex", gap: "1rem", marginTop: "6px" }}>
        {relatedDocumentFields.slice(0, showMaxLen).map((docField) => (
          <div
            key={`${docField.documentId}_${docField.valuePath}`}
            style={{ backgroundColor: "#F3F3F3", borderRadius: "6px", padding: "1rem", flex: 1, cursor: "pointer" }}
            onClick={() => handleViewAll({ relatedChunks, relatedDocumentFields })}
          >
            <SummaryWrapperStyled noOfLines={3}>
              <UI_BODY_BOLD_SM_DF_JSX>{docField.fieldLabel}</UI_BODY_BOLD_SM_DF_JSX>
              <UI_CAPTION_DF_JSX style={{ color: colors2.grey60, marginTop: "0.2rem" }}>
                {docField.documentTitle}
              </UI_CAPTION_DF_JSX>
            </SummaryWrapperStyled>
          </div>
        ))}
      </div>
    </div>
  );
};

/** Direct-link variant: all refs in horizontal scroll, each card links directly to the annotation page */
const DirectLinkRefs = ({
  relatedChunks,
  relatedChunksFetchStatus,
  refetchRelatedChunks,
  relatedDocumentFields = [],
}) => {
  const { SELF_MANAGED_BASE_CONFIGS } = useGetQueryData("platformConfigs");

  const getChunkHref = (chunk) => getRoutePathToPublishedContent({
    contentType: chunk.meta?.contentType || chunk.meta?.kp_content_type,
    contentId: chunk.meta?.documentId,
    SELF_MANAGED_BASE_CONFIGS,
    scrollIntoViewElemId: chunk.meta?.valuePath,
  });

  const getDocFieldHref = (docField) => getRoutePathToPublishedContent({
    contentType: docField.contentType,
    contentId: docField.documentId,
    SELF_MANAGED_BASE_CONFIGS,
    scrollIntoViewElemId: docField.valuePath,
  });

  const cardStyle = {
    backgroundColor: "#F3F3F3", borderRadius: "6px",
    padding: "0.75rem", height: "100%",
  };
  const linkStyle = {
    textDecoration: "none", color: "inherit",
    flexShrink: 0, minWidth: "180px", maxWidth: "240px",
  };

  return (
    <ChunksFetchWrapper relatedChunksFetchStatus={relatedChunksFetchStatus} refetchRelatedChunks={refetchRelatedChunks}>
      {(relatedChunks?.data?.length > 0 || relatedDocumentFields.length > 0) && (
        <div>
          <UI_TAG style={{ marginBottom: "6px" }}>REFERENCES</UI_TAG>
          <div style={{ display: "flex", gap: "0.75rem", overflowX: "auto", paddingBottom: "4px" }}>
            {relatedChunks?.data?.map((chunk) => (
              <a key={chunk._id} target="_blank" href={getChunkHref(chunk)} style={linkStyle}>
                <div style={cardStyle}>
                  <SummaryWrapperStyled noOfLines={3}>
                    <CONTENT_BODY_POSTIT>{chunk.annotations?.fragment?.allText}</CONTENT_BODY_POSTIT>
                  </SummaryWrapperStyled>
                </div>
              </a>
            ))}
            {relatedDocumentFields.map((docField) => (
              <a key={`${docField.documentId}_${docField.valuePath}`} target="_blank" href={getDocFieldHref(docField)} style={linkStyle}>
                <div style={cardStyle}>
                  <UI_BODY_BOLD_SM_DF_JSX>{docField.fieldLabel}</UI_BODY_BOLD_SM_DF_JSX>
                  <UI_CAPTION_DF_JSX style={{ color: colors2.grey60, marginTop: "0.2rem" }}>
                    {docField.documentTitle}
                  </UI_CAPTION_DF_JSX>
                </div>
              </a>
            ))}
          </div>
        </div>
      )}
    </ChunksFetchWrapper>
  );
};
